import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import type { DocumentType, FactoryDocument } from '@indutrust/factory-model';
import { EnvironmentService } from '@indutrust/shared-env';
import { BaseApiClient } from '@indutrust/shared-model';
import type { Observable } from 'rxjs';

/**
 * API client for the factory-scoped document sub-resource at
 * `/factories/{factoryId}/documents`.
 *
 * Call the inherited CRUD methods with
 * `urlSegments: { factoryId: <id> }` to bind the URL to a specific factory,
 * e.g. `getAll({ factoryId: 42 })`.
 *
 * For binary uploads use {@link FactoryDocumentApiClient.upload | upload}
 * — the inherited `add` posts JSON, which the backend won't accept for
 * the actual file payload.
 */
@Service()
export class FactoryDocumentApiClient extends BaseApiClient<FactoryDocument> {
  protected envService = inject(EnvironmentService);
  protected http = inject(HttpClient);

  protected template: Partial<FactoryDocument> = {};

  /**
   * Uploads a binary document for a factory using `multipart/form-data`.
   *
   * Mirrors the `update_logo` precedent set by
   * {@link import('../factory/factory-actions-api-client.service').FactoryActionsApiClient | FactoryActionsApiClient}
   * — we let the browser pick the boundary by passing a `FormData`
   * instance directly instead of going through the JSON `add` path on
   * {@link BaseApiClient}.
   *
   * @example
   * client.upload(42, file, 'trade_license').subscribe();
   */
  upload(
    factoryId: number,
    file: File,
    category: DocumentType
  ): Observable<FactoryDocument> {
    const form = new FormData();
    form.append('attached_file', file, file.name);
    form.append('category', category);
    return this.http.post<FactoryDocument>(
      `${this.envService.getApiUrl()}/factories/${factoryId}/documents`,
      form
    );
  }

  get url(): string {
    return `${this.envService.getApiUrl()}/factories/{factoryId}/documents`;
  }
}
